import React from 'react';
import { Award, Users, ShoppingBag, Receipt, DollarSign, TrendingUp, Sparkles } from 'lucide-react';
import { useWaitersReport } from '../../hooks/useReports';
import { WaiterPerformance } from '../../services/ReportsService';

interface Props {
  from: string;
  to: string;
}

function formatCOP(val: number) {
  return '$ ' + Math.round(val).toLocaleString('es-CO');
}

export function WaitersTab({ from, to }: Props) {
  const { data, isLoading } = useWaitersReport(from, to);

  if (isLoading) {
    return (
      <div className="py-20 text-center">
        <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm font-medium text-text-muted">Calculando rendimiento del equipo de meseros...</p>
      </div>
    );
  }

  const waiters = data?.waiters ?? [];

  if (waiters.length === 0) {
    return (
      <div className="rounded-3xl border border-border bg-surface p-12 text-center shadow-sm">
        <Users className="mx-auto mb-3 h-10 w-10 text-text-muted" />
        <p className="text-base font-semibold text-text">Sin ventas registradas por personal</p>
        <p className="text-xs text-text-muted mt-1">
          No hay órdenes pagadas en el período seleccionado ({from} a {to}).
        </p>
      </div>
    );
  }

  const top3 = waiters.slice(0, 3);

  return (
    <div className="space-y-6">
      {/* ── Podio de Honor de los 3 Mejores Vendedores ── */}
      <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <span className="rounded-lg bg-amber-500/20 p-1.5 text-amber-500">
            <Award className="h-4 w-4" />
          </span>
          <h2 className="text-base font-bold text-text">Podio de Rendimiento del Personal</h2>
          <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[10px] font-bold text-amber-600 dark:text-amber-400">
            {waiters.length} colaboradores activos
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {top3.map((w, index) => {
            const borderColors = [
              'border-amber-400 bg-amber-500/10',
              'border-slate-300 bg-slate-500/10',
              'border-amber-700/50 bg-amber-700/10',
            ];
            const medal = ['🥇 1er Lugar', '🥈 2do Lugar', '🥉 3er Lugar'][index];

            return (
              <div
                key={w.userId}
                className={`rounded-2xl border p-4 shadow-sm flex flex-col justify-between ${borderColors[index] || 'border-border bg-surface'}`}
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-text">{medal}</span>
                    <span className="rounded-full bg-surface px-2 py-0.5 text-[10px] font-semibold text-text-muted">
                      {w.ordersCount} órdenes
                    </span>
                  </div>
                  <h3 className="font-extrabold text-text text-base truncate">{w.userName}</h3>
                  <p className="text-[11px] text-text-muted capitalize">{w.role}</p>
                </div>

                <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between text-xs">
                  <div>
                    <p className="text-[10px] text-text-muted">Ventas Totales</p>
                    <p className="font-bold text-text text-sm">{formatCOP(w.totalSales)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-text-muted">Ticket Promedio</p>
                    <p className="font-semibold text-emerald-500">{formatCOP(w.avgTicket)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Tabla de Clasificación Completa ── */}
      <div className="overflow-hidden rounded-3xl border border-border bg-surface shadow-sm">
        <div className="border-b border-border bg-surface-muted/50 px-5 py-4">
          <h3 className="text-sm font-bold text-text">Ranking Completo de Ventas y Propinas</h3>
          <p className="text-xs text-text-muted mt-0.5">
            Detalle por colaborador ordenado por facturación total generada
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-border bg-surface-muted/30 text-text-muted uppercase text-[10px] font-bold tracking-wider">
              <tr>
                <th className="py-3 px-4">Posición</th>
                <th className="py-3 px-4">Colaborador</th>
                <th className="py-3 px-4">Rol</th>
                <th className="py-3 px-4 text-center">Mesas</th>
                <th className="py-3 px-4 text-center">Órdenes</th>
                <th className="py-3 px-4 text-right">Ticket Promedio</th>
                <th className="py-3 px-4 text-right">Propinas Generadas</th>
                <th className="py-3 px-4 text-right font-extrabold text-text">Total Ventas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {waiters.map((w) => (
                <tr key={w.userId} className="hover:bg-surface-muted/40 transition-colors">
                  <td className="py-3.5 px-4 font-bold text-sm">
                    {w.medal}
                  </td>
                  <td className="py-3.5 px-4 font-bold text-text">
                    {w.userName}
                  </td>
                  <td className="py-3.5 px-4 text-text-muted capitalize">
                    {w.role}
                  </td>
                  <td className="py-3.5 px-4 text-center text-text font-medium">
                    {w.tablesServed}
                  </td>
                  <td className="py-3.5 px-4 text-center text-text font-medium">
                    {w.ordersCount}
                  </td>
                  <td className="py-3.5 px-4 text-right text-emerald-600 dark:text-emerald-400 font-semibold">
                    {formatCOP(w.avgTicket)}
                  </td>
                  <td className="py-3.5 px-4 text-right text-amber-500 font-semibold">
                    {formatCOP(w.totalTips)}
                  </td>
                  <td className="py-3.5 px-4 text-right font-extrabold text-sm text-text">
                    {formatCOP(w.totalSales)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
